/**
 * 演示站首页 — /demo（对齐概念稿 design/pages/demo.html 逐区块复刻）。
 *
 * 数据全部为 demoData.ts 静态合成数据（虚构品牌「星驰 X3 电动车」，
 * 竞品「风行者 S5」「蓝鲸 E7」），免登录、不接真实租户接口。
 * 仅留资卡 POST /api/demo/leads 真实落库。
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import StatCard from "../../components/StatCard";
import HeatMatrix from "../../components/HeatMatrix";
import AlertItem from "../../components/AlertItem";
import FunnelView from "../../components/FunnelView";
import Badge from "../../components/Badge";
import { submitLead } from "../../api/demo";
import { scrollToLeadForm, DEMO_CTA_SX } from "./DemoLayout";
import {
  DEMO_ALERTS,
  DEMO_COMPETITORS,
  DEMO_COMPETITOR_NOTE,
  DEMO_FUNNEL,
  DEMO_FUNNEL_NOTE,
  DEMO_MATRIX_CELLS,
  DEMO_PLATFORMS,
  DEMO_STATS,
  DEMO_TOPICS,
} from "./demoData";

/** 卡头（对齐 .card-head：标题左 + 徽标/链接右）。 */
function CardHead({ title, extra }: { title: string; extra?: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
      {extra}
    </Box>
  );
}

export default function DemoHome() {
  // ---- 留资表单状态 ----
  const [companyName, setCompanyName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  /** 提交留资：成功→成功态；失败→错误提示可重试。 */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    if (!companyName.trim() || !contact.trim()) {
      setSubmitError("请填写公司名和联系方式");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        company_name: companyName.trim(),
        contact: contact.trim(),
        message: message.trim() || undefined,
        source_page: "/demo",
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "提交失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
      {/* ===== 首屏品牌标语卡 ===== */}
      <Card>
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: "text.primary" }}>
            你的品牌，AI 说了算吗？
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.disabled", mt: 1 }}>
            FHSAI GEO 智能增长平台 · AI 搜索时代的增长新引擎
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", mt: 2 }}>
            <Button onClick={scrollToLeadForm} sx={DEMO_CTA_SX}>
              预约真实演示
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                document.getElementById("matrix")?.scrollIntoView({ behavior: "smooth" })
              }
              sx={{ color: "text.secondary", borderColor: "divider" }}
            >
              查看全景矩阵
            </Button>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 1.5 }}>
            以下为虚构品牌「星驰 X3 电动车」的合成演示数据 · 竞品：风行者 S5、蓝鲸 E7
          </Typography>
        </CardContent>
      </Card>

      {/* ===== 指标带 ×4 ===== */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {DEMO_STATS.map((s) => (
          <StatCard
            key={s.title}
            title={s.title}
            value={s.value}
            suffix={"suffix" in s ? s.suffix : undefined}
            subtitle={s.subtitle}
            trend={"trend" in s ? s.trend : undefined}
          />
        ))}
      </Box>

      {/* ===== 全景矩阵 + 预警流 ===== */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" }, gap: 2 }}>
        <Card id="matrix">
          <CardContent>
            <CardHead title="AI 可见性 · 全景矩阵" extra={<Badge label="1 格失联" variant="error" />} />
            <HeatMatrix
              topics={DEMO_TOPICS}
              platforms={DEMO_PLATFORMS}
              cells={DEMO_MATRIX_CELLS}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <CardHead
              title="预警流"
              extra={
                <>
                  <Badge label="P0 ×3" variant="error" />
                  <Badge label="P1 ×6" variant="warning" />
                </>
              }
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {DEMO_ALERTS.map((item) => (
                <AlertItem key={item.id} item={item} demoMode />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ===== 三层漏斗 + 竞品对比 ===== */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card>
          <CardContent>
            <CardHead title="三层漏斗 · 本月" />
            <FunnelView summary={DEMO_FUNNEL} />
            <Divider sx={{ my: 1.75, borderColor: "lineSoft" }} />
            <Typography sx={{ fontSize: 12, color: "text.disabled", lineHeight: 1.8 }}>
              {DEMO_FUNNEL_NOTE}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <CardHead title="我方 vs 竞品 · AI 可见性" extra={<Badge label="本周" variant="info" />} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {DEMO_COMPETITORS.map((c) => (
                <Box
                  key={c.name}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr 64px",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{c.name}</Typography>
                  <Box
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: "semanticBg.neutral",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${c.widthPct}%`,
                        borderRadius: 5,
                        bgcolor: c.self ? "primary.main" : "text.disabled",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", textAlign: "right" }}
                  >
                    {c.score}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 1.75, borderColor: "lineSoft" }} />
            <Typography sx={{ fontSize: 12, color: "text.disabled", lineHeight: 1.8 }}>
              {DEMO_COMPETITOR_NOTE}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* ===== 留资卡 ===== */}
      <Card id="lead-form">
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
            获取你品牌的真实 GEO 诊断
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.disabled", mt: 1 }}>
            留下公司与联系方式，运营团队将为你开通正式账户并跑一轮真实监测
          </Typography>

          {submitted ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mt: 2.5,
                color: "success.main",
              }}
            >
              <CheckCircleOutline />
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                已收到，1 个工作日内联系
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "center",
                alignItems: "flex-start",
                flexWrap: "wrap",
                mt: 2.5,
              }}
            >
              <TextField
                label="公司名"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{ width: 220 }}
              />
              <TextField
                label="联系方式（手机 / 微信）"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{ width: 220 }}
              />
              <TextField
                label="留言（可选）"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: 500 }}
                sx={{ width: 260 }}
              />
              <Button type="submit" disabled={submitting} sx={{ ...DEMO_CTA_SX, height: 40 }}>
                {submitting ? "提交中…" : "预约真实演示"}
              </Button>
            </Box>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mt: 2, maxWidth: 480, mx: "auto", fontSize: 13 }}>
              {submitError}
            </Alert>
          )}
          {!submitted && (
            <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 1.5 }}>
              提交后 1 个工作日内联系
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ===== 页脚：合成数据声明 ===== */}
      <Typography sx={{ textAlign: "center", fontSize: 12, color: "text.disabled", mt: 1, pb: 3 }}>
        本页所有数据为合成示例，不代表任何真实品牌 · FHSAI 平台体验演示站
      </Typography>
    </Box>
  );
}
